import { useCallback, useState } from 'react';

import { queryPerformanceCenterTextVariationsConnectedRequestsStatus } from 'src/graphql/performance';
import { alignChannelWithBackend } from 'src/data/channel';
import { CIP_ADS_PER_PAGE } from 'src/data/performance';
import { useCurrentWorkspaceId, useNotificationActions, usePerformanceActions } from 'src/store';
import { promisedWait } from 'src/lib/common';

const EXTRACTION_POLLING_INTERVAL = 5000;

const waitForCopiesExtraction = async ({
  channel,
  workspaceId,
  notifications,
  options = null,
  deleteResource,
  setExtractingCopies,
  getChannelLastExtraction,
}) => {
  if (options?.abortSignal?.aborted) {
    return;
  }
  const alignedChannel = alignChannelWithBackend(channel);

  const result = await queryPerformanceCenterTextVariationsConnectedRequestsStatus(
    alignedChannel,
    workspaceId
  );

  const isIntegrationExtractionStated = result?.length;

  if (!isIntegrationExtractionStated) {
    // Wait sometime and check again
    await promisedWait(EXTRACTION_POLLING_INTERVAL);

    return waitForCopiesExtraction({
      channel,
      workspaceId,
      notifications,
      options,
      deleteResource,
      setExtractingCopies,
      getChannelLastExtraction,
    });
  }

  // if extraction started we wanna move the poling to the performance store
  // in case the user closed the insights panel
  return getChannelLastExtraction(channel);
};

const channelAbortControllers = new Map();

export const useListenToIntegrationExtraction = () => {
  const performanceActions = usePerformanceActions();
  const currentWorkspaceId = useCurrentWorkspaceId();
  const notifications = useNotificationActions();
  const [accountId, setAccountId] = useState(null);

  const cancelListenToIntegrationExtraction = useCallback((channel) => {
    if (channelAbortControllers.has(channel)) {
      channelAbortControllers.get(channel)?.abort();
      channelAbortControllers.delete(channel);
    } else {
      channelAbortControllers.forEach((channelAbortController) => {
        channelAbortController.abort();
      });
      channelAbortControllers.clear();
    }
  }, []);

  const listenToIntegrationExtraction = useCallback(
    async (channel) => {
      if (!channel) {
        return;
      }
      const channelLastExtractionDate = performanceActions.getChannelLastExtractionDate(channel);
      if (channelLastExtractionDate) {
        cancelListenToIntegrationExtraction();
        return;
      }

      channelAbortControllers.set(channel, new AbortController());

      const setExtractingCopies = (channel, extracting) => {
        performanceActions.setExtractingCopies(channel, extracting);
      };

      const getChannelLastExtraction = (channel) => {
        performanceActions.getChannelLastExtraction(channel);
      };

      try {
        const result = await waitForCopiesExtraction({
          channel,
          workspaceId: currentWorkspaceId,
          notifications,
          options: {
            abortSignal: channelAbortControllers.get(channel)?.signal,
          },
          textVariationsResultsCount: CIP_ADS_PER_PAGE,
          setExtractingCopies,
          getChannelLastExtraction,
        });
        if (result?.[0]?.accountIds) {
          performanceActions.initSelectedAccountIds(result[0]?.accountIds, channel);
          setAccountId(result[0]?.accountIds);
        }
        if (result?.[0]?.updatedAt) {
          performanceActions.initLastExtractionDate(result[0]?.updatedAt, channel);
        }
      } finally {
        setExtractingCopies(channel, false);
        channelAbortControllers.delete(channel);
      }
    },
    [performanceActions, currentWorkspaceId, notifications, cancelListenToIntegrationExtraction]
  );

  return {
    listenToIntegrationExtraction,
    cancelListenToIntegrationExtraction,
    accountId,
  };
};
